'use client';
import { useModalContext } from '#pplx/frontend/aether/modals/context/useModalContext.js';
import { useToastContext } from '#pplx/frontend/aether/toasts/context/useToastContext.js';
import { useMicrosoftPicker } from '#pplx/frontend/components/hooks/useMicrosoftPickerSupport.js';
import { MICROSOFT_FORMATTED_NAME } from '#pplx/frontend/components/utils/consts.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
const SharepointSiteModalDynamic = Dynamic(async () => {
    const { SharepointSiteModal } = await import('#pplx/frontend/apps/ask/microsoft/SharepointSiteModal.js');
    return { default: SharepointSiteModal };
});
export const useMicrosoftPickerModal = ({ mode, }) => {
    const intl = useIntl();
    const [isOpen, setIsOpen] = useState(false);
    const [options, setOptions] = useState(null);
    const { openToast } = useToastContext();
    const { openModal } = useModalContext();
    const { openPicker, toPickerDocument } = useMicrosoftPicker();
    const onOpen = useCallback((options) => {
        // The microsoft file picker modal is composed of two views: the site selector
        // and the file picker. The site selector only shows when the user is connecting
        // to sharepoint and contains a search bar input that does not work on pages with
        // an ask input. The exact reason is still unknown, but it appears like the ask input
        // is stealing focus from the search bar. However, When the site selector is opened
        // via the modal state provider, the search bar becomes focusable.
        // TODO [ENT-2189]: Move this entire modal into the modal state provider instead of
        // just the site selector view
        if (options.connector === 'sharepoint' && mode === 'attachment') {
            openModal(SharepointSiteModalDynamic, {
                connectionUuid: options.connectionUuid,
                onSelectSite: (siteWebUrl) => {
                    if (options) {
                        setIsOpen(true);
                        setOptions({ ...options, webUrl: siteWebUrl });
                        openPicker({ ...options, webUrl: siteWebUrl });
                    }
                },
            }, { replace: true });
        }
        else {
            // If onedrive, then go straight to the file picker view
            // If sharepoint, then go to site list view
            setIsOpen(true);
            setOptions(options);
            if (options.connector === 'onedrive') {
                openPicker(options);
            }
        }
    }, [mode, openModal, openPicker]);
    const onClose = useCallback(() => {
        setIsOpen(false);
        setOptions(null);
    }, [setIsOpen, setOptions]);
    const onError = useCallback((error) => {
        logger.error(`A ${MICROSOFT_FORMATTED_NAME} file picker error occured:`, error);
        onClose();
        const typedError = error;
        let message;
        switch (typedError?.code) {
            case 'POPUP_BLOCKED':
                message = intl.formatMessage({
                    defaultMessage: "We couldn't open the Microsoft sign-in window. Please allow pop-ups for this site in your browser and try again.",
                    id: '1aeec8711e',
                    description: 'Toast error message shown when the Microsoft sign-in popup is blocked by the browser',
                });
                break;
            case 'TIMEOUT':
                message = intl.formatMessage({
                    defaultMessage: 'Microsoft is taking longer than usual. This may be a network issue. Please try again.',
                    id: 'e20889a6f4',
                    description: 'Toast error message shown when the Microsoft file picker connection times out',
                });
                break;
            case 'AUTH':
                message = intl.formatMessage({
                    defaultMessage: "We couldn't sign in to Microsoft. Please check your Microsoft account access and try again.",
                    id: '900abf8822',
                    description: 'Toast error message shown when Microsoft authentication fails',
                });
                break;
            default:
                message = intl.formatMessage({
                    defaultMessage: 'Microsoft had an unexpected error. Please try again in a few moments.',
                    id: '829f302178',
                    description: 'Toast error message shown when the Microsoft file picker encounters an unexpected error',
                });
        }
        openToast({
            message,
            variant: 'error',
            timeoutSeconds: 5,
        });
    }, [onClose, openToast, intl]);
    const onSelectSite = useCallback((siteWebUrl) => {
        if (options) {
            setOptions({ ...options, webUrl: siteWebUrl });
            openPicker({ ...options, webUrl: siteWebUrl });
        }
    }, [options, openPicker]);
    return useMemo(() => ({
        onOpen,
        onClose,
        onError,
        onSelectSite,
        toPickerDocument,
        isOpen,
        options,
    }), [isOpen, onClose, onError, onOpen, onSelectSite, options, toPickerDocument]);
};
//# sourceMappingURL=useMicrosoftPickerModal.js.map